import axios from 'axios'
import type {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import { useAuthStore } from '@/stores/auth'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
})

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('accessToken')
  if (token && config.headers && !config.url?.includes('token/refresh')) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false

type FailedRequest = {
  resolve: (token: string) => void
  reject: (err: AxiosError) => void
}

let failedQueue: FailedRequest[] = []

const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token!)
    }
  })
  failedQueue = []
}

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      const refreshToken = localStorage.getItem('refreshToken')

      if (!refreshToken) {
        const authStore = useAuthStore()
        authStore.clearAuth()
        return Promise.reject(error)
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${token}`,
              }
              resolve(api(originalRequest))
            },
            reject: (err: AxiosError) => reject(err),
          })
        })
      }

      isRefreshing = true

      try {
        const response = await axios.post(`${import.meta.env.VITE_API_URL}token/refresh/`, {
          refresh: refreshToken,
        })

        const newAccessToken = response.data.access
        const newRefreshToken = response.data.refresh

        localStorage.setItem('accessToken', newAccessToken)
        localStorage.setItem('refreshToken', newRefreshToken)

        api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
        processQueue(null, newAccessToken)

        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccessToken}`,
        }

        return api(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null)
        const authStore = useAuthStore()
        authStore.clearAuth()
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  },
)

interface User {
  id: string
  username: string
  email: string
  first_name: string
  last_name: string
  role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
  is_active: boolean
  date_joined: string
  last_login: string | null
  created_by: string | null
  owner_id: string | null
  created_by_name: string
  owner_name: string
}

interface UserForm {
  username: string
  email: string
  first_name: string
  last_name: string
  role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
  password?: string
  confirmPassword?: string
  is_active?: boolean
}

export const authService = {
  async login(email: string, password: string) {
    const response = await api.post('auth/login/', { email, password })
    return response.data
  },
  async register(userData: {
    username: string
    email: string
    password: string
    confirmPassword: string
    first_name: string
    last_name: string
  }) {
    const response = await axios.post(`${import.meta.env.VITE_API_URL}auth/register/`, userData)
    return response.data
  },
  async logout(refreshToken: string) {
    return await api.post('auth/logout/', {
      refresh_token: refreshToken,
    })
  },
  async getUserProfile() {
    const response = await api.get('auth/profile/')
    return response.data
  },
  async refreshToken(refresh: string) {
    return axios.post(`${import.meta.env.VITE_API_URL}token/refresh/`, { refresh })
  },
  async createUser(userData: UserForm) {
    const response = await api.post('auth/create-supervisor/', userData)
    return response.data
  },
  async getUsers(role?: string): Promise<User[]> {
    const params = role ? { role } : {}
    const response = await api.get('auth/users/', { params })
    return response.data
  },
  async updateUser(id: string, userData: Partial<UserForm>) {
    const response = await api.put(`auth/users/${id}/`, userData)
    return response.data
  },
  async deleteUser(id: string) {
    const response = await api.delete(`auth/users/${id}/delete/`)
    return response.data
  },
  async toggleUserActive(id: string, is_active: boolean): Promise<void> {
    await api.patch(`auth/users/${id}/toggle-active/`, { is_active })
  },
  async updateProfile(userData: Partial<UserForm>) {
    const response = await api.put('auth/profile/', userData)
    return response.data
  },
  async requestPasswordReset(email: string) {
    const response = await api.post('auth/password-reset/', { email })
    return response.data
  },
  async confirmPasswordReset(data: { uid: string; token: string; password: string; confirmPassword: string }) {
    const response = await api.post('auth/password-reset-confirm/', data)
    return response.data
  },
}






// import type { AxiosError, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
// import axios from 'axios'
// import { useAuthStore } from '@/stores/auth'

// export const api = axios.create({
//   baseURL: import.meta.env.VITE_API_URL,
//   headers: {
//     'Content-Type': 'application/json',
//   },
//   withCredentials: true, // always send cookies
// })

// // Public endpoints that do NOT require Authorization header
// const PUBLIC_ENDPOINTS = ['auth/login/', 'auth/register/']

// // Request interceptor
// api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
//   config.withCredentials = true
//   const token = localStorage.getItem('accessToken')

//   // Skip Authorization for public endpoints
//   if (token && config.headers && !PUBLIC_ENDPOINTS.some((url) => config.url?.includes(url))) {
//     config.headers.Authorization = `Bearer ${token}`
//   }

//   return config
// })

// // Queue for handling token refresh
// let isRefreshing = false
// type FailedRequest = { resolve: (token: string) => void; reject: (err: AxiosError) => void }
// let failedQueue: FailedRequest[] = []

// const processQueue = (error: AxiosError | null, token: string | null) => {
//   failedQueue.forEach((prom) => {
//     if (error) prom.reject(error)
//     else prom.resolve(token!)
//   })
//   failedQueue = []
// }

// // Response interceptor for 401 token refresh
// api.interceptors.response.use(
//   (response: AxiosResponse) => response,
//   async (error: AxiosError) => {
//     const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean }

//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true
//       const refreshToken = localStorage.getItem('refreshToken')

//       if (!refreshToken) {
//         useAuthStore().clearAuth()
//         return Promise.reject(error)
//       }

//       if (isRefreshing) {
//         return new Promise((resolve, reject) => {
//           failedQueue.push({
//             resolve: (token: string) => {
//               originalRequest.headers = {
//                 ...originalRequest.headers,
//                 Authorization: `Bearer ${token}`,
//               }
//               resolve(api(originalRequest))
//             },
//             reject: (err: AxiosError) => reject(err),
//           })
//         })
//       }

//       isRefreshing = true
//       try {
//         const response = await api.post('token/refresh/', { refresh: refreshToken })
//         const newAccessToken = response.data.access
//         const newRefreshToken = response.data.refresh

//         localStorage.setItem('accessToken', newAccessToken)
//         localStorage.setItem('refreshToken', newRefreshToken)

//         api.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`
//         processQueue(null, newAccessToken)

//         originalRequest.headers = {
//           ...originalRequest.headers,
//           Authorization: `Bearer ${newAccessToken}`,
//         }

//         return api(originalRequest)
//       } catch (refreshError) {
//         processQueue(refreshError as AxiosError, null)
//         useAuthStore().clearAuth()
//         return Promise.reject(refreshError)
//       } finally {
//         isRefreshing = false
//       }
//     }

//     return Promise.reject(error)
//   },
// )

// // Types
// interface User {
//   id: string
//   username: string
//   email: string
//   first_name: string
//   last_name: string
//   role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
//   is_active: boolean
//   date_joined: string
//   last_login: string | null
//   created_by: string | null
//   owner_id: string | null
//   created_by_name: string
//   owner_name: string
// }

// interface UserForm {
//   username: string
//   email: string
//   first_name: string
//   last_name: string
//   role: 'ADMIN' | 'SUPERVISOR' | 'CUSTOMER' | 'EMPLOYEE'
//   password?: string
//   confirmPassword?: string
//   is_active?: boolean
// }

// // Auth Service
// export const authService = {
//   async login(email: string, password: string) {
//     const response = await api.post('auth/login/', { email, password })
//     return response.data
//   },
//   async register(userData: {
//     username: string
//     email: string
//     password: string
//     confirmPassword: string
//     first_name: string
//     last_name: string
//   }) {
//     const response = await api.post('auth/register/', userData)
//     return response.data
//   },
//   async logout(refreshToken: string) {
//     const response = await api.post('auth/logout/', { refresh_token: refreshToken })
//     return response.data
//   },
//   async getUserProfile() {
//     const response = await api.get('auth/profile/')
//     return response.data
//   },
//   async refreshToken(refresh: string) {
//     const response = await api.post('token/refresh/', { refresh })
//     return response.data
//   },
//   async createUser(userData: UserForm) {
//     const response = await api.post('auth/create-supervisor/', userData)
//     return response.data
//   },
//   async getUsers(role?: string): Promise<User[]> {
//     const params = role ? { role } : {}
//     const response = await api.get('auth/users/', { params })
//     return response.data
//   },
//   async updateUser(id: string, userData: Partial<UserForm>) {
//     const response = await api.put(`auth/users/${id}/`, userData)
//     return response.data
//   },
//   async deleteUser(id: string) {
//     const response = await api.delete(`auth/users/${id}/delete/`)
//     return response.data
//   },
//   async toggleUserActive(id: string, is_active: boolean): Promise<void> {
//     await api.patch(`auth/users/${id}/toggle-active/`, { is_active })
//   },
// }

